import { ReactElement, useMemo } from 'react';
import { interpolate, spring } from 'remotion';

// Components
import { TeamMemberCard } from './TeamMemberCard';

// Utils
import { calculateCardPositions, splitIntoPhases } from './utils';
import {
  calculateCategoryDurationFromCount,
  calculatePhaseDuration,
  getSceneExitProgress,
  isSceneExitActive,
} from '../../utils';
import { combineClasses } from '@/utils';

// Themes
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Font
import { lalezarFontFamily } from '@/styles/fonts';

// Constants
import {
  CARD_HEIGHT,
  CARD_WIDTH,
  CATEGORY_TRANSITION_GAP,
  EXIT_DURATION,
  LABEL_EXIT_LEAD_FRAMES,
  LABEL_SLIDE_DISTANCE,
} from './constants';
import { CLAMP_BOTH } from '@/constants';
import { FALLBACK_SRC } from '@shared/constants';

// Types
import { CardPosition, Group, PhaseAnimation, TeamMember } from '../../types';

// Hooks
import { usePhaseAnimations } from './usePhaseAnimations';

const LABEL_SPRING_CONFIG = {
  damping: 14,
  stiffness: 90,
  mass: 0.8,
};

interface GroupLabelProps {
  label: string;
  startFrame: number;
  duration: number;
  fps: number;
  currentFrame: number;
  sceneExitStartFrame?: number;
  sceneExitDuration?: number;
  isLastGroup?: boolean;
}

const GroupLabel = ({
  label,
  startFrame,
  duration,
  fps,
  currentFrame,
  sceneExitStartFrame,
  sceneExitDuration,
  isLastGroup = false,
}: GroupLabelProps): ReactElement => {
  const { colors, typography } = employeeYearInReviewTheme;
  const localFrame = currentFrame - startFrame;

  // Entrance animation
  const enterProgress = spring({
    frame: localFrame,
    fps,
    config: LABEL_SPRING_CONFIG,
  });

  const enterTranslateX = interpolate(
    enterProgress,
    [0, 1],
    [-LABEL_SLIDE_DISTANCE, 0],
  );
  const enterOpacity = interpolate(enterProgress, [0, 0.5], [0, 1], {
    extrapolateRight: 'clamp',
  });

  const sceneExitProgress = getSceneExitProgress(
    currentFrame,
    sceneExitStartFrame,
    sceneExitDuration,
  );

  // Scene exit: label slides left and fades out
  const sceneExitTranslateX = interpolate(
    sceneExitProgress,
    [0, 1],
    [0, -LABEL_SLIDE_DISTANCE],
  );
  const sceneExitOpacity = interpolate(
    sceneExitProgress,
    [0, 1],
    [1, 0],
    CLAMP_BOTH,
  );

  // Per-group exit: label exits before next category (skipped for last group)
  const exitStartFrame = duration - EXIT_DURATION - LABEL_EXIT_LEAD_FRAMES;
  const groupExitProgress =
    !isLastGroup && sceneExitProgress === 0
      ? interpolate(
          localFrame - exitStartFrame,
          [0, EXIT_DURATION],
          [0, 1],
          CLAMP_BOTH,
        )
      : 0;

  const groupExitTranslateX = interpolate(
    groupExitProgress,
    [0, 1],
    [0, -LABEL_SLIDE_DISTANCE],
  );
  const groupExitOpacity = interpolate(
    groupExitProgress,
    [0, 1],
    [1, 0],
    CLAMP_BOTH,
  );

  const exitTranslateX = sceneExitTranslateX + groupExitTranslateX;
  const exitOpacity = sceneExitOpacity * groupExitOpacity;
  const translateX = enterTranslateX + exitTranslateX;
  const opacity = enterOpacity * exitOpacity;

  return (
    <div
      className="absolute top-16 left-16 z-10"
      style={{
        transform: `translateX(${translateX}px)`,
        opacity,
      }}
    >
      <p
        className={combineClasses(typography.lg, colors.textPrimary)}
        style={{
          fontFamily: lalezarFontFamily,
        }}
      >
        {label}
      </p>
    </div>
  );
};

export interface GroupSectionProps {
  group: Group;
  groupStartFrame: number;
  containerWidth: number;
  fps: number;
  currentFrame: number;
  isLastGroup: boolean;
  /** Only the active group (by timeline) is visible */
  isActiveGroup: boolean;
  sceneExitStartFrame?: number;
  sceneExitDuration?: number;
}

export const GroupSection = ({
  group,
  groupStartFrame,
  containerWidth,
  fps,
  currentFrame,
  isLastGroup,
  isActiveGroup,
  sceneExitStartFrame,
  sceneExitDuration,
}: GroupSectionProps): ReactElement => {
  const { members } = group;
  const phases = useMemo(() => splitIntoPhases(members), [members]);
  const hasMultiplePhases = phases.length > 1;

  // Phase 1 configuration
  const phase1Members = phases[0] ?? [];
  const phase1StartFrame = groupStartFrame;
  const phase1HasExit = hasMultiplePhases || !isLastGroup;
  const phase1Duration = hasMultiplePhases
    ? calculatePhaseDuration(phase1Members.length)
    : 0;

  // Phase 2 configuration
  const phase2Members = phases[1] ?? [];
  const phase2StartFrame =
    groupStartFrame + phase1Duration + EXIT_DURATION + CATEGORY_TRANSITION_GAP;
  const phase2HasExit = !isLastGroup;

  // Phase 1 positions and animations
  const phase1Positions = useMemo(
    () =>
      calculateCardPositions(phase1Members.length, containerWidth, CARD_WIDTH),
    [phase1Members.length, containerWidth],
  );

  // Get animations for each phase
  const phase1Animations = usePhaseAnimations({
    members: phase1Members,
    phaseStartFrame: phase1StartFrame,
    fps,
    currentFrame,
    hasExit: phase1HasExit,
    sceneExitStartFrame,
    sceneExitDuration,
  });

  // Phase 2 positions and animations
  const phase2Positions = useMemo(
    () =>
      calculateCardPositions(phase2Members.length, containerWidth, CARD_WIDTH),
    [phase2Members.length, containerWidth],
  );

  const phase2Animations = usePhaseAnimations({
    members: phase2Members,
    phaseStartFrame: phase2StartFrame,
    fps,
    currentFrame,
    hasExit: phase2HasExit,
    sceneExitStartFrame,
    sceneExitDuration,
  });

  const groupDuration = calculateCategoryDurationFromCount(members.length);
  const hideGroup = !isActiveGroup;
  const isSceneExitPhase = isSceneExitActive(
    currentFrame,
    sceneExitStartFrame,
    sceneExitDuration,
  );

  // Hide phase 1 during scene exit when group has 2 phases (shows only last 2 cards)
  const hidePhase1DuringExit =
    isLastGroup && hasMultiplePhases && isSceneExitPhase;

  const renderPhase = (
    phaseMembers: TeamMember[],
    positions: CardPosition[],
    animations: PhaseAnimation[],
    forceHide = false,
  ): ReactElement[] => {
    if (forceHide || !phaseMembers.length) return [];

    return phaseMembers.reduce<ReactElement[]>((acc, member, index) => {
      const position = positions[index];
      const animation = animations[index];
      if (!position || !animation) return acc;

      const { id, firstName, lastName, badgeText, imageUrl } = member;

      acc.push(
        <TeamMemberCard
          key={id}
          firstName={firstName}
          lastName={lastName}
          badgeText={badgeText}
          imageUrl={imageUrl || FALLBACK_SRC.AVATAR}
          flipProgress={animation.flipProgress}
          width={CARD_WIDTH}
          height={CARD_HEIGHT}
          style={{
            position: 'absolute',
            left: position.x,
            top: position.y,
            transform: `translateY(${animation.translateY}px) rotate(${member.rotation}deg)`,
            opacity: animation.opacity,
          }}
        />,
      );

      return acc;
    }, []);
  };

  return (
    <div
      style={{
        opacity: hideGroup ? 0 : 1,
        ...(hideGroup && { pointerEvents: 'none' }),
      }}
    >
      {/* Group label */}
      <GroupLabel
        label={group.label}
        startFrame={groupStartFrame}
        duration={groupDuration}
        fps={fps}
        currentFrame={currentFrame}
        sceneExitStartFrame={sceneExitStartFrame}
        sceneExitDuration={sceneExitDuration}
        isLastGroup={isLastGroup}
      />

      {/* Phase 1 */}
      {renderPhase(
        phase1Members,
        phase1Positions,
        phase1Animations,
        hidePhase1DuringExit,
      )}

      {/* Phase 2 */}
      {hasMultiplePhases &&
        renderPhase(phase2Members, phase2Positions, phase2Animations)}
    </div>
  );
};
